import axios from "axios";

import appConfig from "../config";

// Create axios instance with base configuration
const apiClient = axios.create({ baseURL: appConfig.API_BASE_URL });

// Request interceptor to add auth header
apiClient.interceptors.request.use(
  (config) => {
    config.headers["x-origin"] = appConfig.ORIGIN;
    // Add authorization header if not explicitly disabled
    if (!config.skipAuth) {
      const token = localStorage.getItem("accessToken");
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// Response interceptor for error handling
apiClient.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    // Handle common errors
    if (error.response?.status === 401) {
      // Check if token is actually expired before removing it
      const token = localStorage.getItem("accessToken");
      if (token) {
        // Import isTokenExpired function here to avoid circular imports
        const isTokenExpired = (token) => {
          try {
            const base64Url = token.split(".")[1];
            const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
            const jsonPayload = decodeURIComponent(
              atob(base64)
                .split("")
                .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
                .join(""),
            );
            const decoded = JSON.parse(jsonPayload);
            if (!decoded || !decoded.exp) return true;
            const currentTime = Date.now() / 1000;
            return decoded.exp < currentTime;
          } catch (error) {
            console.error("Error decoding token:", error);
            return true; // If we can't decode, assume expired
          }
        };

        // Only remove token if it's actually expired
        if (isTokenExpired(token)) {
          localStorage.removeItem("accessToken");
        }
      }
    }
    return Promise.reject(error);
  },
);

// API client methods
export const api = {
  /**
   * GET request
   *
   * @param {string} path - API endpoint path
   * @param {boolean} skipAuth - Whether to skip authorization header (default: false)
   * @param {object} config - Additional axios config
   * @returns {Promise} Axios response
   */
  get: (path, skipAuth = false, config = {}) => {
    return apiClient.get(path, { skipAuth, ...config });
  },

  /**
   * POST request
   *
   * @param {string} path - API endpoint path
   * @param {object} body - Request body data
   * @param {boolean} skipAuth - Whether to skip authorization header (default: false)
   * @param {object} config - Additional axios config
   * @returns {Promise} Axios response
   */
  post: (path, body = {}, skipAuth = false, config = {}) => {
    return apiClient.post(path, body, { skipAuth, ...config });
  },

  /**
   * PUT request
   *
   * @param {string} path - API endpoint path
   * @param {object} body - Request body data
   * @param {boolean} skipAuth - Whether to skip authorization header (default: false)
   * @param {object} config - Additional axios config
   * @returns {Promise} Axios response
   */
  put: (path, body = {}, skipAuth = false, config = {}) => {
    return apiClient.put(path, body, { skipAuth, ...config });
  },

  patch: (path, body = {}, skipAuth = false, config = {}) => {
    return apiClient.patch(path, body, { skipAuth, ...config });
  },

  /**
   * DELETE request
   *
   * @param {string} path - API endpoint path
   * @param {boolean} skipAuth - Whether to skip authorization header (default: false)
   * @param {object} config - Additional axios config
   * @returns {Promise} Axios response
   */
  delete: (path, skipAuth = false, config = {}) => {
    return apiClient.delete(path, { skipAuth, ...config });
  },
};

export default api;
